import { isValidDate, monthsInitials } from 'features/quotation/helpers';
import { IChargedAgainstOption, IOperators, IValidatingCarrier } from 'features/transactions/types';
import {
	CountryCodeEnum,
	DateToFromFilterModel,
	FilterModel,
	FilterType,
	IEnvelopeList,
	IHeadCell,
	IMonthLabelValue,
	ISort,
	OperatorFilterEnum,
	orderTypes,
	SelectFilterListData,
	SelectFilterModelMultiple,
} from './types';
import { monthsNames } from './constants';
// @ts-ignore
import * as Excel from 'exceljs/dist/exceljs.min.js';
import * as ExcelProper from 'exceljs';
// @ts-ignore
import * as fs from 'file-saver';
import moment from 'moment/moment';
import { customAlphabet } from 'nanoid';
import { customNanoIdAlphabet, NIMBUX_CRM_APP_BASE_URL } from '../../constants/constants';

const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const EXCEL_EXTENSION = '.xlsx';

export const convertFiltersToRecord = (
	filters: FilterModel[],
): Record<string, string | number | boolean> | undefined => {
	if (filters.length > 0) {
		const record = {};
		filters.forEach((filter) => {
			if (filter instanceof SelectFilterModelMultiple) {
				// @ts-ignore
				record[filter.key] = filter.value?.join(',');
			} else if (filter instanceof DateToFromFilterModel) {
				// @ts-ignore
				record[filter.key] = filter.value;
				// @ts-ignore
				record[filter.secondKey] = filter.secondValue;
			} else {
				// @ts-ignore
				record[filter.key] = filter.value;
			}
		});
		return record;
	}
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const createSortHandler =
	(property: string, sortFields: ISort[], sortableFields: string[], setSortFields: (values: ISort[]) => void) =>
	(): void => {
		let newSortsValues: ISort[];
		const field = sortFields.find((x) => x.field === property);
		if (field === undefined) {
			newSortsValues = [...sortFields, { field: property, order: 'asc' }];
		} else {
			const fieldsUpdated = [...sortFields];
			fieldsUpdated.splice(sortFields.indexOf(field), 1);
			if (field.order === 'asc') {
				field.order = 'desc';
				fieldsUpdated.push(field);
			}
			newSortsValues = fieldsUpdated;
		}
		newSortsValues.sort((a, b) => sortableFields.indexOf(a.field) - sortableFields.indexOf(b.field));
		setSortFields(newSortsValues);
	};

export const getSortableFields = (headers: IHeadCell[]): string[] =>
	headers.filter((x) => x.sortable).map((x) => x.field);

export const formatSortingFields = (sortingFields: ISort[]): string =>
	sortingFields.map((x) => (x.order === 'asc' ? x.field : `-${x.field}`)).join(',');

export function checkShowErrorMessage(show: boolean, message: string | undefined): string {
	if (show && message) {
		return message;
	}
	return '';
}

export const getFilterModalTitle = (filter: FilterModel): string => {
	switch (filter.type) {
		case FilterType.STRING:
			return stringFilterTitle(filter);
		case FilterType.NUMERIC:
			return numericFilterTitle(filter);
		case FilterType.DATE:
		case FilterType.TIME:
		case FilterType.LIST:
			return `${filter.label} igual a`;
		default:
			return filter.operator === OperatorFilterEnum.BETWEEN ? `${filter.label} se encuentra en el rango` : filter.label;
	}
};

function stringFilterTitle(filter: FilterModel) {
	switch (filter.operator) {
		case OperatorFilterEnum.CONTAINS:
			return ` ${filter.label} contiene`;
		case OperatorFilterEnum.ENDS:
			return ` ${filter.label} termina en`;
		case OperatorFilterEnum.STARTS:
			return `${filter.label} comienza con`;
		default:
			return `${filter.label} igual a`;
	}
}
function numericFilterTitle(filter: FilterModel) {
	switch (filter.operator) {
		case OperatorFilterEnum.GREATER:
			return `${filter.label} es mayor a`;
		case OperatorFilterEnum.GREATEROREQUAL:
			return `${filter.label} es mayor o igual a`;
		case OperatorFilterEnum.LESSER:
			return `${filter.label} es menor a`;
		case OperatorFilterEnum.LESSEROREQUAL:
			return `${filter.label} es menor o igual a`;
		default:
			return `${filter.label} igual a`;
	}
}

export function getValidStringDate(date: unknown): string[] | '' {
	return date && isValidDate(date as Date) ? (date as Date).toISOString().split('T')[0].split('-') : '';
}

export function getValidStringTime(date: unknown): string | '' {
	return date && isValidDate(date as Date) ? (date as Date).toISOString().split('T')[1] : '';
}

// Los tres formateadores de abajo hacen split() sobre el valor crudo que viene de las APIs.
// Si el campo no viene (undefined/null), el split explota y, sin error boundary, se lleva
// puesta la pantalla entera. Por eso validan el tipo antes de tocar el string.
export function formatDateToShowUser(dateValue: string | null | undefined): string {
	if (typeof dateValue !== 'string' || !dateValue) return '';
	const dateValueSplit = dateValue.split('-');
	return Array.isArray(dateValueSplit) && dateValueSplit.length === 3
		? `${dateValueSplit[2]}/${dateValueSplit[1]}/${dateValueSplit[0]}`
		: '';
}

export function formatDateTimeToShowUser(dateTimeValue: string | null | undefined): string {
	if (typeof dateTimeValue !== 'string' || !dateTimeValue) return '';
	const dateTimeValueSplit = dateTimeValue.split('T');
	if (!Array.isArray(dateTimeValueSplit) || dateTimeValueSplit.length !== 2) return '';
	const timePart = dateTimeValueSplit[1].replace(/\.\d+/, '').replace(/Z$/i, '');
	return `${formatDateToShowUser(dateTimeValueSplit[0])} ${timePart}`;
}

export function formatDateTimeToShowUserFromSaleOrderApiValue(dateTimeValue: string | null | undefined): string {
	if (typeof dateTimeValue !== 'string' || !dateTimeValue) return '';
	const dateTimeValueSplit = dateTimeValue.split('T');
	return Array.isArray(dateTimeValueSplit) && dateTimeValueSplit.length === 2
		? `${formatDateToShowUser(dateTimeValueSplit[0])} ${dateTimeValueSplit[1].substr(0, 8)}`
		: '';
}

export const isMobile = {
	Android: (): RegExpMatchArray | null => navigator.userAgent.match(/Android/i),
	BlackBerry: (): RegExpMatchArray | null => navigator.userAgent.match(/BlackBerry/i),
	iOS: (): RegExpMatchArray | null => navigator.userAgent.match(/iphone4/i),
	Opera: (): RegExpMatchArray | null => navigator.userAgent.match(/Opera Mini/i),
	Windows: (): RegExpMatchArray | null =>
		navigator.userAgent.match(/IEMobile/i) || navigator.userAgent.match(/WPDesktop/i),
	any: (): RegExpMatchArray | null =>
		isMobile.Android() || isMobile.BlackBerry() || isMobile.iOS() || isMobile.Opera() || isMobile.Windows(),
};
export function getEnvelopedListPageTotal(envelopedList: IEnvelopeList<any>): number {
	return Math.ceil(envelopedList.headers['X-Page-Total'] / envelopedList.headers['X-Page-Size']);
}

export function convertValidatingCarrierDataToFilter(data: IValidatingCarrier[]): SelectFilterListData[] {
	return data.map((carrier) => {
		return { id: carrier.code, name: carrier.name };
	});
}

export function convertChargedAgainstOptionDataToFilter(data: IChargedAgainstOption[]): SelectFilterListData[] {
	return data.map((option) => {
		return { id: option.code, name: option.name };
	});
}
export function convertOperatorsDataToFilter(data: IOperators[]): SelectFilterListData[] {
	return data.map((operator) => {
		return { id: operator.value, name: operator.description };
	});
}

export function getMonthName(monthNumber: number): string {
	if (monthNumber >= 1 && monthNumber <= 12) {
		return monthsNames[monthNumber - 1];
	} else throw new Error('invalid month number');
}

interface ColorOptions {
	header: string;
}

interface LogoOptions {
	position: string;
	mergeCells: string;
}

interface GenerateExcelOptions {
	data: Record<string, unknown>[][];
	title: string;
	headers: string[];
	logo: LogoOptions;
	color: ColorOptions;
	wrapCell: number[];
	widthColumns: number[];
	filename: string;
}

export interface ExportExcelOptions {
	title: string;
	headers: IHeadCell[];
	widthColumns: number[];
	filename: string;
}

export function exportToExcel<T>(
	data: T[],
	options: ExportExcelOptions,
	transformValue: (key: string, value: any, row: T) => any,
): void {
	if (options.headers.length != options.widthColumns.length) {
		console.error('The length of the widthColumns array must be equal to the length of the headers array');
		return;
	}
	const logoPosition =
		String.fromCharCode(65 + options.widthColumns.length - 2) +
		'1:' +
		String.fromCharCode(65 + options.widthColumns.length - 1) +
		'3';
	const mergeCell = 'A1:' + String.fromCharCode(65 + options.widthColumns.length - 1) + '3';

	const optionToGenerate = {
		data: transformDataToExport(data, options.headers, transformValue),
		filename: options.filename,
		title: options.title,
		widthColumns: options.widthColumns,
		logo: { position: logoPosition, mergeCells: mergeCell },
		wrapCell: [1, 2, 3],
		color: {
			header: 'c393f9',
		},
	} as GenerateExcelOptions;

	generateExcelFile(optionToGenerate);
}

function renameKeys(obj: Record<string, unknown>, newKeys: Record<string, unknown>): Record<string, unknown>[] {
	const keyValues = Object.keys(obj).map((key) => {
		const newKey = newKeys[key] || key;
		// @ts-ignore
		return { [newKey]: obj[key] };
	});
	return Object.assign({}, ...keyValues);
}

function transformDataToExport<T>(
	itemList: T[],
	headerColumns: IHeadCell[],
	transformValue: (key: string, value: any, row: T) => any,
): Record<string, unknown>[][] {
	const modelKeys = {};
	const changeModelKeys = {};
	const newItemList = [];

	for (const entry of headerColumns) {
		// @ts-ignore
		modelKeys[entry.field] = null;
		// @ts-ignore
		changeModelKeys[entry.field] = entry.headerName;
	}

	for (const item of itemList) {
		const newModelTransform = {};
		// @ts-ignore
		Object.keys(modelKeys).forEach((key) => (newModelTransform[key] = transformValue(key, item[key], item)));
		newItemList.push(renameKeys(newModelTransform, changeModelKeys));
	}

	return newItemList;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
function generateExcelFile(option: GenerateExcelOptions) {
	// Excel Title, Header, Data
	const title = option['title'] || 'Listado';
	const header = Object.keys(option['data'][0] || []);
	const data = option['data'].map((i) => Object.values(i));

	// Create workbook
	const workbook: ExcelProper.Workbook = new Excel.Workbook();
	workbook.creator = 'Mevuelo';
	workbook.created = new Date();

	// Create worksheet
	const worksheet = workbook.addWorksheet('Sheet1');

	const logoBase64 =
		'data:image/png;base64,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';

	// Add Row and formatting
	const titleRow = worksheet.addRow([title]);
	titleRow.font = { name: 'Arial', family: 4, size: 16, underline: false, bold: true };

	// Add Image
	const logo = workbook.addImage({
		base64: logoBase64,
		extension: 'png',
	});

	worksheet.addImage(logo, option['logo']['position']);
	worksheet.mergeCells(option['logo']['mergeCells']);

	// Add Header Row
	const headerRow = worksheet.addRow(header);
	headerRow.font = { bold: true };

	// Cell Style : Fill and Border
	headerRow.eachCell((cell) => {
		cell.fill = {
			type: 'pattern',
			pattern: 'solid',
			fgColor: { argb: 'FF' + option['color']['header'] },
			bgColor: { argb: 'FF0000FF' },
		};
		cell.border = {
			top: { style: 'thin' },
			left: { style: 'thin' },
			bottom: { style: 'thin' },
			right: { style: 'thin' },
		};
	});

	// Add Data and Conditional Formatting
	data.forEach((d, index) => {
		// eslint-disable-next-line no-prototype-builtins
		const indexImage = d.findIndex((x) => typeof x === 'object' && x.hasOwnProperty('image'));
		let image;
		let position;
		let height;
		if (indexImage !== -1) {
			image = workbook.addImage({
				base64: String(d[indexImage].image),
				extension: 'png',
			});
			// @ts-ignore
			const init = d[indexImage].rowStart + index;
			position = d[indexImage].position + init + ':' + d[indexImage].position + init;
			height = d[indexImage]['height'];
			// @ts-ignore
			d[indexImage] = '';
		}
		const row = worksheet.addRow(d);
		if (indexImage !== -1) {
			// @ts-ignore
			worksheet.addImage(image, position);
			// @ts-ignore
			row.height = height;
		}

		// eslint-disable-next-line no-prototype-builtins
		if (option.hasOwnProperty('wrapCell')) {
			// @ts-ignore
			if (option['wrapCell'].length > 0) {
				// @ts-ignore
				for (let j = 0; j < option['wrapCell'].length; j++) {
					// @ts-ignore
					const wrapC = row.getCell(option['wrapCell'][j]);
					wrapC.alignment = { wrapText: true, vertical: 'top' };
				}
			}
		}
	});

	// Fixing width column
	if (option['widthColumns'].length > 0) {
		for (let i = 0; i < option['widthColumns'].length; i++) {
			worksheet.getColumn(i + 1).width = option['widthColumns'][i];
		}
	}

	// Generate Excel File with given name
	workbook.xlsx.writeBuffer().then((dataToExport) => {
		// @ts-ignore
		const blob = new Blob([dataToExport], { type: EXCEL_TYPE });
		fs.saveAs(blob, option['filename'] + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
	});
}

export const fillMonthsWithYearForExplorer = (): IMonthLabelValue[] => {
	const currentMonth = moment().month();
	const monthsCurrentYear = [];
	const monthsNextYear = [];
	const currentYear = new Date().getFullYear();
	for (let i = 0; i <= 11; i++) {
		const year = currentMonth > i ? currentYear + 1 : currentYear;
		const obj = { label: `${monthsInitials[i].toLowerCase()}. ${year}`, value: i + 1 };
		if (year == currentYear) monthsCurrentYear.push(obj);
		else monthsNextYear.push(obj);
	}
	return [...monthsCurrentYear, ...monthsNextYear];
};

export const nanoidGenerator = customAlphabet(customNanoIdAlphabet, 32);

export function isAllowOrderTypeInCountry(orderTypeId: number, countryCode: CountryCodeEnum): boolean {
	return orderTypes.some((orderType) => orderType.id === orderTypeId && orderType.countriesAllow.includes(countryCode));
}

export function isItauVN(countryCode: CountryCodeEnum): boolean {
	return countryCode === CountryCodeEnum.TN_ITAU_PY;
}

export const dashIfNone = (val?: string | number | null): string | number => (val ? val : '-');

const isPowerProductionHost = (): boolean =>
	typeof window !== 'undefined' && window.location.hostname === 'power.mevuelo.com';

/** Caso en Nimbux CRM: prod → crm.mevuelo.com; local/dev/testing → base.nimbux.tech */
export const getDetailOfCaseInCRM = (caseId: number): string => {
	if (isPowerProductionHost()) {
		return `https://crm.mevuelo.com/dashboard/cases/${caseId}`;
	}
	return `https://base.nimbux.tech/dashboard/cases/${caseId}`;
};
export const getDetailOfClientInCRM = (clientId: string): string =>
	`${NIMBUX_CRM_APP_BASE_URL}dashboard/clients/${clientId}`;

export const getDetailOfDealOrCaseInCRM = (crmTicket: number, isBitrixDeal: boolean): string => {
	if (isBitrixDeal) {
		return `https://mevuelo.bitrix24.es/crm/deal/details/${crmTicket}/`;
	}
	return getDetailOfCaseInCRM(crmTicket);
};

export const getValueNotEmptyOrNull = (value: string | null): string | null => (value && value != '' ? value : null);

export function base64ToBlob(base64: string, contentType = 'application/pdf'): Blob {
	const byteCharacters = atob(base64);
	const byteNumbers = new Array(byteCharacters.length);

	for (let i = 0; i < byteCharacters.length; i++) {
		byteNumbers[i] = byteCharacters.charCodeAt(i);
	}

	const byteArray = new Uint8Array(byteNumbers);
	return new Blob([byteArray], { type: contentType });
}
